import { LANG_BCP47, LANGUAGES } from "./config.js";

const rtf =
  typeof Intl !== "undefined" && Intl.RelativeTimeFormat
    ? new Intl.RelativeTimeFormat(undefined, { numeric: "auto" })
    : null;

export function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.round((then - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (!rtf) return new Date(iso).toLocaleString();
  if (abs < 60) return rtf.format(diffSec, "second");
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), "hour");
  return rtf.format(Math.round(diffSec / 86400), "day");
}

function langLabel(code) {
  return LANGUAGES.find((l) => l.code === code)?.label || code.toUpperCase();
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function setExpanded(article, expanded) {
  article.classList.toggle("is-expanded", expanded);
  const toggle = article.querySelector(".article__expand");
  const body = article.querySelector(".article__body");
  if (toggle) {
    toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    toggle.setAttribute("aria-label", expanded ? "Collapse" : "Expand");
    toggle.title = expanded ? "Collapse" : "Expand";
  }
  if (body) body.hidden = !expanded;
}

export function renderArticles(container, articles) {
  container.replaceChildren();
  const frag = document.createDocumentFragment();

  for (const a of articles) {
    const article = document.createElement("article");
    article.className = "article" + (a.image ? " has-image" : "");
    article.dataset.lang = a.lang;
    article.dataset.id = a.id;
    article.lang = LANG_BCP47[a.lang] || a.lang;

    const meta = document.createElement("div");
    meta.className = "article__meta";
    meta.innerHTML = `
      <span class="article__tag">${escapeHtml(langLabel(a.lang))}</span>
      <span class="article__tag">${escapeHtml(a.sector)}</span>
      <span class="article__source">${escapeHtml(a.source)}</span>
      <span class="article__time">${escapeHtml(relativeTime(a.publishedAt))}</span>
    `;
    article.appendChild(meta);

    if (a.image) {
      const img = document.createElement("img");
      img.className = "article__media";
      img.src = a.image;
      img.alt = "";
      img.loading = "lazy";
      img.decoding = "async";
      img.width = 640;
      img.height = 360;
      img.addEventListener("error", () => {
        img.remove();
        article.classList.remove("has-image");
      });
      article.appendChild(img);
    }

    const h2 = document.createElement("h2");
    h2.className = "article__title";
    h2.textContent = a.title;
    h2.dataset.defineText = "1";
    article.appendChild(h2);

    const body = document.createElement("div");
    body.className = "article__body";
    body.hidden = true;

    if (a.summary) {
      const p = document.createElement("p");
      p.className = "article__summary";
      p.textContent = a.summary;
      p.dataset.defineText = "1";
      body.appendChild(p);
    } else {
      const p = document.createElement("p");
      p.className = "article__summary article__summary--empty";
      p.textContent = "No summary in the feed — open the source for the full article.";
      body.appendChild(p);
    }

    const source = document.createElement("a");
    source.className = "article__source-link";
    source.href = a.url;
    source.target = "_blank";
    source.rel = "noopener noreferrer";
    source.title = "Open at source";
    source.setAttribute("aria-label", "Open at source");
    source.innerHTML = `<svg class="article__source-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.75"/><path d="M3 12h18M12 3c2.5 2.8 3.8 5.8 3.8 9s-1.3 6.2-3.8 9c-2.5-2.8-3.8-5.8-3.8-9S9.5 5.8 12 3z" fill="none" stroke="currentColor" stroke-width="1.75"/></svg>`;
    body.appendChild(source);

    article.appendChild(body);

    const actions = document.createElement("div");
    actions.className = "article__actions";

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "article__expand";
    toggle.setAttribute("aria-expanded", "false");
    toggle.setAttribute("aria-label", "Expand");
    toggle.title = "Expand";
    toggle.innerHTML = `<svg class="article__expand-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    toggle.addEventListener("click", (e) => {
      e.stopPropagation();
      setExpanded(article, !article.classList.contains("is-expanded"));
    });
    actions.appendChild(toggle);
    article.appendChild(actions);

    frag.appendChild(article);
  }

  container.appendChild(frag);
}

export function renderErrors(el, errors) {
  if (!errors?.length) {
    el.hidden = true;
    el.replaceChildren();
    return;
  }
  el.hidden = false;
  el.replaceChildren();
  const seen = new Set();
  for (const e of errors) {
    const msg = e.message || `Couldn't reach ${e.source}.`;
    if (seen.has(msg)) continue;
    seen.add(msg);
    const p = document.createElement("p");
    p.textContent = msg;
    el.appendChild(p);
  }
}
